import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { type Language, type TranslationKey, translations } from "./translations";
import { usePageContext } from "vike-react/usePageContext";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const getUrlLang = (pathname: string | undefined): Language => {
  if (!pathname) return "pl";
  const match = pathname.match(/^\/(pl|en|de)(\/|$)/);
  if (match) return match[1] as Language;
  return "pl";
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const pageContext = usePageContext();

  // Derived language from URL is the source of truth for rendering
  const currentUrlLang = getUrlLang(pageContext.urlPathname);

  const [languageState, setLanguageState] = useState<Language>(currentUrlLang);

  // Sync state if URL changes (client-side navigation)
  useEffect(() => {
    const urlLang = getUrlLang(pageContext.urlPathname);
    if (urlLang !== languageState) {
      setLanguageState(urlLang);
    }
  }, [pageContext.urlPathname, languageState]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = currentUrlLang;
    }
  }, [currentUrlLang]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem("language", lang);
    }
  }, []);

  const t = useCallback(
    (key: TranslationKey): string => {
      const entry = translations[key];
      if (!entry) return key;
      // Always use currentUrlLang to ensure consistency with the URL
      return entry[currentUrlLang];
    },
    [currentUrlLang]
  );

  return (
    <LanguageContext.Provider value={{ language: currentUrlLang, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
